import axios from 'axios';
import _, { cloneDeep } from 'lodash';
import { resources } from '../i18n';
import { geocodeByAddress } from 'react-places-autocomplete';
import {
  ASSETS_SECTION,
  BANK_ACCOUNT_SECTION,
  BANK_ACCOUNT_SECTION_2,
  BANK_ACCOUNT_SECTION_3,
  BUYER_ENTITY_TYPE,
  BUYER_SECTION,
  DL_FIELD_NUMBER,
  ENTITY_TYPE,
  IS_INDIVIDUAL_ENTITY,
  DL_NUMBER_FIELD,
  DocElementType,
  EMPLOYMENT_SECTION,
  ENTITY_MEMBER_DL_DOB_INFIX,
  ENTITY_MEMBER_DL_LICENSE_INFIX,
  ENTITY_MEMBER_DL_STATE_INFIX,
  ENTITY_MEMBER_SSN_INFIX,
  ENTITY_TRUSTEE_MEMBERS_LABEL,
  FIELDS_CHECK_VALIDATIONS,
  LIABILITIES_SECTION,
  LOAN_UPLOAD_NAME,
  MAILING_ADDRESS_ADDRESS,
  MAILING_ADDRESS_CITY,
  MAILING_ADDRESS_STATE,
  MAILING_ADDRESS_ZIP,
  NAME_OF_ACCOUNT_NAME,
  SELLER_PERCENTAGE_FIELD,
  SELLER_SHARED_GROUP_ID,
  SELLER_SPOUSE_GROUP_ID,
  SELLER_MARITAL_STATUS_FIELD,
  SELLER_IS_SPOUSE_FIELD_PREFIX,
  ADDITIONAL_SELLER_PARENT_ID,
  SOI_SECTION_ID,
  SSN_FIELDS,
  ACCOUNT_NAME,
  ID_ONLY_SECTION_ID,
} from './constants';
import { addressVerification } from '../services/order/benutech';

import { ID_MAPPING_VERIFF } from './constants';
import { getDecryptedBankAcc } from '../services/order/order';

export const randomUUID = () => {
  let u = Date.now().toString(16) + Math.random().toString(16) + '0'.repeat(16);
  let result = [u.substr(0, 8), u.substr(8, 4), '4000-8' + u.substr(13, 3), u.substr(16, 12)].join(
    '-'
  );
  return result;
};

export const sortElements = (data) => {
  const groupedElements = _.orderBy(data, ['parent_element', 'parent_value', 'id']);
  const outputArray = [];

  groupedElements.forEach((item) => {
    const idx = outputArray.findIndex((elem) => elem.element_id === item.element_id);
    const children =
      groupedElements.filter((child) => child.parent_element === item.element_id) || [];
    if (idx === -1) {
      outputArray.push(item);
      if (children?.length) outputArray.push(...children);
    } else {
      if (children?.length) outputArray.splice(idx + 1, 0, ...children);
    }
  });
  return outputArray;
};

export const isBankAccountNumberField = (element) =>
  Boolean(
    element?.is_bank_account_number_field ||
    element?.is_individual_bank_account_number_field ||
    element?.is_entity_bank_account_number_field
  );

export const isBankRoutingNumberField = (element) =>
  Boolean(
    element?.is_bank_routing_number_field ||
    element?.is_individual_bank_routing_number_field ||
    element?.is_entity_bank_routing_number_field
  );

export const isBankAccountNameField = (element) =>
  Boolean(
    element?.is_bank_account_name_field ||
    element?.is_individual_bank_account_name_field ||
    element?.is_entity_bank_account_name_field
  );

export const isNameOnAccountField = (element) =>
  Boolean(element?.label?.replace(':', '') === NAME_OF_ACCOUNT_NAME) ||
  isBankAccountNameField(element);

/** Name-on-account fields split into First/Middle/Last, except Entity's — that stays a plain text field. */
export const isSplitBankAccountNameField = (element) =>
  isNameOnAccountField(element) && !element?.is_entity_bank_account_name_field;

/** The "Who owns this bank account?" Individual/Entity radio question. */
export const isBankOwnerTypeField = (element) => Boolean(element?.is_bank_owner_type_field);

export const errorChecker = (element, value, mask = '', isSeparateParticipantForm = false) => {
  let tempValue = value;
  let regexPattern = element?.match_pattern;
  if (regexPattern === '^\\w+@[a-zA-Z_]+?\\.[a-zA-Z]{2,4}$') regexPattern = null;
  if (
    (element?.label?.includes('Bank Account Number') ||
      element?.label?.includes('Bank ABA') ||
      element?.label?.includes('Routing Number')) &&
    element?.required &&
    value?.length < 25
  ) {
    regexPattern = '^[0-9]+$';
  }
  if (mask && tempValue) {
    const emptyMask = mask.replace(/9/g, '_');
    if (emptyMask === tempValue) {
      tempValue = '';
    }
  }
  if (element?.element_name === SELLER_PERCENTAGE_FIELD) {
    if (!tempValue || !Object.keys(tempValue || {})?.length) {
      return { isError: true, message: 'This field is required' };
    }
    if (Object.values(tempValue || {}).some((value) => !value?.length)) {
      return { isError: true, message: 'Please provide appropriate value for this field.' };
    }
    if (Object.values(tempValue || {}).some((value) => value > 100)) {
      return { isError: true, message: 'Please provide appropriate value for this field.' };
    }
    if (Object.values(tempValue || {}).some((value) => value < 0)) {
      return { isError: true, message: 'Please provide appropriate value for this field.' };
    }
    const total = Object.values(tempValue || {}).reduce((acc, value) => acc + +value, 0);
    if (total !== 100 && !isSeparateParticipantForm) {
      return { isError: true, message: 'Please provide appropriate value for this field.' };
    }
    return { isError: false, message: '' };
  } else if (isSplitBankAccountNameField(element) && element.required) {
    if (!tempValue?.length) return { isError: true, message: 'This field is required' };
    if (tempValue?.includes(',')) {
      const splittedArr = tempValue?.split(',') || [];
      if (!splittedArr[0]?.length || !splittedArr[2]?.length) {
        return { isError: true, message: 'This field is required' };
      } else {
        return { isError: false, message: '' };
      }
    } else {
      return { isError: false, message: '' };
    }
  } else if (element.required && !tempValue) {
    return { isError: true, message: 'This field is required' };
  } else if (regexPattern && tempValue && !element?.label?.toLowerCase()?.includes('zip')) {
    const re = new RegExp(regexPattern);
    if (!re.test(tempValue)) {
      return { isError: true, message: 'Please provide appropriate value for this field.' };
    } else if (element.element_name === 'core_cc_exp') {
      const d = new Date();
      const m = parseInt(tempValue.split('/')[0]),
        y = parseInt(tempValue.split('/')[1]);

      if (
        m > 12 ||
        y < d.getFullYear() ||
        y > d.getFullYear() + 100 ||
        (m < d.getMonth() + 1 && y === d.getFullYear())
      ) {
        return { isError: true, message: 'Please provide appropriate value for this field.' };
      } else {
        return { isError: false, message: '' };
      }
    } else {
      return { isError: false, message: '' };
    }
  } else {
    return { isError: false, message: '' };
  }
};

export const areAllElementsSame = (arr) => {
  const uniqueElements = _.uniq(arr);
  return uniqueElements.length === 1;
};

export const verifyElement = (sourceData, value) => {
  let verified = false;
  let message = 'Source found but data does not match';
  let value1 = _.toLower(_.trim(value));
  let foundedSource = '';
  if (sourceData?.verification_source?.length > 0) {
    for (let source of sourceData?.verification_source) {
      for (let source_value of source.value) {
        if (_.isEqual(value1, _.toLower(_.trim(source_value)))) {
          verified = true;
          message = `Data matched with ${source.source} source`;
          foundedSource = source.source;
          break;
        }
      }
    }
  }
  return { verified, message, foundedSource };
};

export const generateAvatarText = (fullName) => {
  const initials = fullName
    ?.split(' ')
    ?.map((name) => name.charAt(0).toUpperCase())
    ?.join('');
  return initials;
};

// Common function that convert static url to dynamic url using param values
export const parseStaticURLToDynamicValues = (breadcrumb, values) => {
  let staticURL = breadcrumb?.url;
  let title = breadcrumb?.title;
  const staticParts = staticURL.split('/');
  let constructedUrl = '';
  for (let i = 0; i < staticParts.length; i++) {
    let part = staticParts[i];
    if (part.startsWith(':')) {
      part = values[part.slice(1)];
    }
    constructedUrl = constructedUrl + (part?.length ? `/${part}` : '');
  }
  return { url: constructedUrl, title: title.startsWith(':') ? values[title.slice(1)] : title };
};

export const fetchSourceData = (element, use = 'auto') => {
  let sources = [];
  if (element.verification?.verification_source?.length) {
    for (let source of element.verification?.verification_source) {
      for (let source_value of source?.value) {
        let tempObj = {
          source: source.source,
          value: source_value,
          is_from_benutech: source.is_from_benutech === 'yes' ? true : false,
          more_details: source?.more_details || [],
        };
        if (source?.hasOwnProperty('is_verified')) {
          tempObj.is_verified = source?.is_verified ? true : false;
        }
        sources.push(tempObj);
      }
    }
    if (element.element_id === 'a1ca9a9c-e068-42b9-89f7-e83e810fce12')
      if (use === 'auto') {
        sources = sources.filter((source) => source.is_from_benutech);
      } else if (use === 'admin') {
        sources = sources;
      } else {
        sources = sources.filter((source) => !source.is_from_benutech);
      }
  }
  return sources;
};

export const convertedValue = (value) => {
  if (value == true) {
    return 'Yes';
  } else {
    return 'No';
  }
};

export const isValidDate = (dateString) => {
  // Replace slashes with dashes in the date string
  const formattedDateString = dateString.replace(/\//g, '-');

  // Try to create a new Date object from the formatted string
  const date = new Date(formattedDateString);
  // Check if the date is a valid date and the formatted string representation of the date is equal to the input
  return {
    isValid: !isNaN(date.getTime()) && formattedDateString === date.toISOString().split('T')[0],
    date,
  };
};

export const findElement = (section, element_id) => {
  if (!section?.groups?.length) return {};
  let elem = {};
  const answer = (element) => {
    if (element?.element_id == element_id) return (elem = element);
    if (element.options.length) {
      for (const option of element?.options) {
        for (const subElement of option?.elements) {
          answer(subElement);
        }
      }
    }
  };
  for (const grp of section?.groups) {
    for (const element of grp?.elements) {
      if (elem?.element_id) return elem;
      answer(element);
    }
  }
  return elem;
};

export const findAddressElements = (section, element_id) => {
  if (!section?.groups?.length) {
    return [];
  }

  let siblings = null;
  let foundIndex = -1;

  const findSiblings = (elements) => {
    if (!elements?.length) return false;

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      if (element?.element_id == element_id) {
        siblings = elements;
        foundIndex = i;
        return true;
      }

      if (element?.options?.length) {
        for (const option of element.options) {
          if (findSiblings(option?.elements)) {
            return true;
          }
        }
      }
    }

    return false;
  };

  for (const grp of section.groups) {
    if (findSiblings(grp?.elements)) break;
  }

  if (!siblings || foundIndex === -1) {
    return [];
  }

  const result = [];
  const maxIndex = Math.min(foundIndex + 5, siblings.length);

  let foundCity = false;
  let foundState = false;
  let foundZip = false;

  for (let i = foundIndex + 1; i < maxIndex; i++) {
    const el = siblings[i];

    const label = el?.label?.toLowerCase() || el?.element?.label?.toLowerCase() || '';

    if (!foundCity && label.includes('city') && el?.display) {
      result.push({ elementId: el.element_id, elementType: 'city', value: '' });
      foundCity = true;
    } else if (!foundState && label.includes('state') && el?.display) {
      result.push({ elementId: el.element_id, elementType: 'state', value: '' });
      foundState = true;
    } else if (
      !foundZip &&
      (label.includes('zip') || label.includes('postal') || label.includes('postcode')) &&
      el?.display
    ) {
      result.push({ elementId: el.element_id, elementType: 'zip', value: '' });
      foundZip = true;
    }
  }

  return result;
};

export const flattenElements = (elements = []) => {
  const flat = [];

  const traverse = (elementList) => {
    for (const el of elementList) {
      flat.push(el); // add current element
      if (el?.options?.length) {
        for (const option of el.options) {
          if (option?.elements?.length) {
            traverse(option.elements);
          }
        }
      }
    }
  };

  traverse(elements);
  return flat;
};

export const findNextElement = (section, element_id) => {
  if (!section?.groups?.length) return null;

  let allElements = [];

  // Flatten elements in traversal order
  const traverse = (element) => {
    allElements.push(element);
    if (element?.options?.length) {
      for (const option of element.options) {
        if (element.user_value && option.option_name !== element.user_value) continue;
        for (const sub of option.elements) {
          traverse(sub);
        }
      }
    }
  };

  // Build full traversal order
  for (const grp of section.groups) {
    for (const element of grp.elements) {
      traverse(element);
    }
  }

  // Find the target element
  const idx = allElements.findIndex((el) => el.element_id === element_id);
  if (idx === -1 || idx === allElements.length - 1) return null;

  // Return the next element in order
  return allElements[idx + 1];
};

const sectionsToSkip = [
  SOI_SECTION_ID,
  BANK_ACCOUNT_SECTION,
  BANK_ACCOUNT_SECTION_2,
  EMPLOYMENT_SECTION,
  LIABILITIES_SECTION,
  ASSETS_SECTION,
  BUYER_SECTION,
  BANK_ACCOUNT_SECTION_3,
  ID_ONLY_SECTION_ID,
];

export const updateMainJsonWithHistory = (participants) => {
  const mainEntry = participants.find((p) => p.main);
  if (!mainEntry) return null;
  const skippedSectionMap = {};
  const mainJson = mainEntry.json;

  for (const participant of participants) {
    if (participant.main) continue;
    const otherJson = participant.json;

    for (const section of otherJson.sections || []) {
      if (sectionsToSkip?.includes(section?.section_id)) {
        if (!skippedSectionMap[section.section_id]) {
          skippedSectionMap[section.section_id] = [];
        }
        skippedSectionMap[section.section_id].push(section);
        continue;
      }

      for (const group of section.groups || []) {
        for (const element of group.elements || []) {
          const processElement = (el) => {
            if (!el?.history?.length || !el?.verified_by_user_id) return;

            const mainSection = mainJson.sections.find((s) => s.section_id === section.section_id);
            if (!mainSection) return;

            const mainElement = findElement(mainSection, el.element_id);
            if (!mainElement) return;

            const missingHistory = !mainElement.history?.length;

            if (missingHistory) {
              mainElement.history = el.history.map((h) => ({
                ...h,
                participant_no: participant.participant_no,
              }));
              mainElement.verified_by_user_id = el.verified_by_user_id;
              const lastHistory = el.history[el.history.length - 1];
              if (
                !lastHistory?.all_field_verified &&
                lastHistory?.to !== undefined &&
                lastHistory.to !== mainElement.user_value &&
                mainElement?.user_value
              ) {
                mainElement.history.push({
                  note: `Participant(${mainEntry?.participant_name}) has updated value after verification`,
                  from: lastHistory?.to,
                  to: mainElement?.user_value,
                  created_by: mainEntry?.participant_name,
                  created_by_user_id: '',
                  approval: 1,
                  created_at: '',
                  from_participant: true,
                });
              } else if (
                lastHistory?.all_field_verified &&
                el?.user_value !== mainElement?.user_value
              ) {
                mainElement.history.push({
                  note: `Participant(${mainEntry?.participant_name}) has updated value after verification`,
                  from: el?.user_value,
                  to: mainElement?.user_value,
                  created_by: mainEntry?.participant_name,
                  created_by_user_id: '',
                  approval: 1,
                  created_at: '',
                  from_participant: true,
                });
              }
            }
          };

          processElement(element);

          if (element.options?.length) {
            for (const option of element.options) {
              for (const subElement of option.elements || []) {
                processElement(subElement);
              }
            }
          }
        }
      }
    }
  }

  // Build new sections array
  const updatedSections = [];
  for (const section of mainJson.sections) {
    if (sectionsToSkip?.includes(section.section_id)) {
      // Add other participants' versions
      const others = skippedSectionMap[section.section_id] || [];
      const sortedOthers = [section, ...others].sort(
        (a, b) => a.participant_number - b.participant_number
      );
      updatedSections.push(...sortedOthers);
    } else {
      updatedSections.push(section);
    }
  }

  return {
    ...mainJson,
    sections: updatedSections,
  };
};

export const updateMainJsonWithHistorySeq = (json, latestPartNo, participantName) => {
  if (!json?.sections?.length) return {};

  let tempJson = cloneDeep(json);
  const answer = (element) => {
    const lastVal =
      element?.history?.[element?.history?.length - 1]?.current_user_value ||
      element?.history?.[element?.history?.length - 1]?.to;
    if (
      element?.history?.length &&
      element?.[`user_value_${latestPartNo}`] &&
      lastVal !== element?.[`user_value_${latestPartNo}`]
    ) {
      element?.history?.push({
        note: `Participant(${participantName}) has updated value after verification`,
        from: lastVal,
        to: element?.[`user_value_${latestPartNo}`],
        created_by: participantName,
        created_by_user_id: '',
        approval: 1,
        created_at: '',
        from_participant: true,
      });
    }
    if (element.options.length) {
      for (const option of element?.options) {
        for (const subElement of option?.elements) {
          answer(subElement);
        }
      }
    }
  };
  for (const section of tempJson.sections) {
    if (sectionsToSkip?.includes(section.section_id)) continue;
    for (const grp of section?.groups) {
      for (const element of grp?.elements) {
        answer(element);
      }
    }
  }
  return tempJson;
};

export const findElementByName = (section, name) => {
  if (!section?.groups?.length) return {};
  let elem = {};
  const answer = (element) => {
    if (element?.element_name.startsWith(name)) return (elem = element);
    if (element.options.length) {
      for (const option of element?.options) {
        for (const subElement of option?.elements) {
          answer(subElement);
        }
      }
    }
  };
  for (const grp of section?.groups) {
    for (const element of grp?.elements) {
      if (elem?.element_id) return elem;
      answer(element);
    }
  }
  return elem;
};

/** Parent participant DL number field from question json (same logic as LastStep). */
export const findDlElementFromJson = (json, participantTypeName, participantNo = 1) => {
  if (!json?.sections?.length) return null;

  const participantType = `${participantTypeName || ''}`.toLowerCase();
  if (!participantType) return null;

  const dlSectionId = participantType?.includes('buyer') ? BUYER_SECTION : SOI_SECTION_ID;
  const section = json?.sections?.find((sec) => sec?.section_id === dlSectionId);
  if (!section) return null;

  const ownerParticipantNo = Number(participantNo) || 1;
  let element = null;

  if (ownerParticipantNo !== 1) {
    element = findElementByName(
      section,
      `${participantType}${DL_FIELD_NUMBER}_${ownerParticipantNo}`
    );
  } else {
    element = findElement(section, DL_NUMBER_FIELD);
  }

  return element?.element_id ? element : null;
};

export const includeSSnFieldsForMember = (json, participantTypeName, participantNo = 1) => {
  if (!json?.sections?.length) return false;

  const participantType = `${participantTypeName || ''}`.toLowerCase();
  if (!participantType) return false;

  const sectionId = participantType?.includes('buyer') ? BUYER_SECTION : SOI_SECTION_ID;
  const section = json?.sections?.find((sec) => sec?.section_id === sectionId);
  if (!section) return false;

  const parentSSNElement = findElementByName(
    section,
    Number(participantNo) === 1
      ? `${participantType}_entity_has_ein`
      : `${participantType}_entity_has_ein_${participantNo}`
  );
  if (!parentSSNElement?.element_id || !parentSSNElement?.display) return false;

  const ssnRelatedOption = parentSSNElement?.options?.find(
    (option) => option?.option_name === 'No'
  );
  const ssnElement = ssnRelatedOption?.elements?.find((element) =>
    element?.element_name?.includes(
      Number(participantNo) === 1
        ? `${participantType}_entity_ssn`
        : `${participantType}_entity_ssn_${participantNo}`
    )
  );

  if (!ssnElement?.element_id || !ssnElement?.display) return false;
  return true;
};

export const findBankAccountElement = (json, label) => {
  if (!json?.sections?.length) return {};
  let elem = {};
  const answer = (element) => {
    if (isBankAccountNumberField(element) || element?.label?.includes(ACCOUNT_NAME)) {
      return (elem = element);
    }
    if (element?.options?.length) {
      for (const option of element?.options) {
        for (const subElement of option?.elements) {
          answer(subElement);
        }
      }
    }
  };
  for (const section of json?.sections) {
    if (section?.is_hidden) continue;
    for (const grp of section?.groups) {
      if (grp?.is_hidden) continue;
      for (const element of grp?.elements) {
        if (elem?.element_id) return elem;
        answer(element);
      }
    }
  }
  return elem;
};

/** `{type}_{templateInfix}_{participantNo}_{memberSequenceNo}` — same as entity member field creation. */
export const getEntityMemberElementName = (
  participantType,
  templateElementName,
  participantNo,
  memberSequenceNo
) => {
  const type = `${participantType || ''}`.toLowerCase();
  const cleaned = `${templateElementName || ''}`.replace(/^_+|_+$/g, '');
  return `${type}_${cleaned}_${Number(participantNo) || 1}_${Number(memberSequenceNo) || 1}`;
};

export const updateJsonIds = (json) => {
  let tempJson = cloneDeep(json);
  const answer = (elem) => {
    elem.element_id = randomUUID();
    if (elem.options.length) {
      for (const option of elem?.options) {
        for (const subElement of option?.elements) {
          answer(subElement);
        }
      }
    }
  };
  for (const elem of tempJson) {
    answer(elem);
  }
  return tempJson;
};

export const modifyJson = (json, elements, section_id, group_id, element_id, option_id) => {
  let tempJson = json;
  let sectionIdx, groupIdx, elementIdx, optionIdx;
  sectionIdx = json.sections.findIndex((section) => section.section_id === section_id);
  if (sectionIdx !== undefined && sectionIdx !== -1) {
    groupIdx = json.sections[sectionIdx].groups.findIndex((group) => group.group_id === group_id);
  } else {
    console.log('Section not found');
    return false;
  }
  if (groupIdx == undefined || groupIdx == -1) {
    console.log('Group not found');
    return false;
  }
  if (element_id && option_id) {
    elementIdx = json.sections[sectionIdx].groups[groupIdx].elements.findIndex(
      (element) => element.element_id === element_id
    );
    if (elementIdx == undefined || elementIdx == -1) {
      console.log('Element not found', elementIdx);
      return false;
    }
    optionIdx = json.sections[sectionIdx].groups[groupIdx].elements[elementIdx].options.findIndex(
      (opt) => opt.option_id === option_id
    );
    if (optionIdx == undefined || optionIdx == -1) {
      console.log('Option not found');

      return false;
    }
    tempJson.sections[sectionIdx].groups[groupIdx].elements[elementIdx].options[
      optionIdx
    ].elements.push(...elements);
  } else {
    tempJson.sections[sectionIdx].groups[groupIdx].elements.push(...elements);
  }
  return tempJson;
};

const isParticipantTypeBlockedForTrustOrLlcAdd = (
  type,
  existingParticipants = [],
  resolveParticipantMeta
) => {
  const primaryIndex = existingParticipants?.findIndex(
    (participant) =>
      String(participant?.user_participant_type_id ?? participant?.participant_type_id) ===
      String(type?.participant_type_id)
  );
  if (primaryIndex === -1) return false;

  const participant = existingParticipants?.[primaryIndex];
  const meta = resolveParticipantMeta?.(participant, primaryIndex) || {};
  const isIndividual =
    meta?.isIndividual ??
    participant?.is_individual ??
    participant?.is_individual_or_entity === 'individual';
  const entityType = (meta?.entityType ?? participant?.entity_type)?.toLowerCase?.()?.trim?.();
  return !isIndividual && (entityType === 'trust' || entityType === 'llc');
};

/** Saved participants (including additional) that match a participant type. */
export const collectParticipantsOfType = (existingParticipants = [], typeId) => {
  if (!typeId) return [];
  const matchesType = (participant) =>
    String(participant?.participant_type_id || participant?.user_participant_type_id || '') ===
    String(typeId);

  return (existingParticipants || []).reduce((rows, participant) => {
    if (participant?.is_new) return rows;
    if (matchesType(participant)) rows.push(participant);
    (participant?.additional_participants || []).forEach((additional) => {
      if (!additional?.is_new && matchesType(additional)) rows.push(additional);
    });
    return rows;
  }, []);
};

export const getParticipantFlowConstraints = (existingParticipants = [], typeId) => {
  const ofType = collectParticipantsOfType(existingParticipants, typeId);
  const hasSequential = ofType.some(
    (participant) => (participant?.is_participant_form_separate_or_single || 'single') === 'single'
  );
  const hasSeparate = ofType.some(
    (participant) => participant?.is_participant_form_separate_or_single === 'separate'
  );

  if (!ofType.length || hasSequential) {
    return {
      hasSequential,
      hasSeparate,
      allowed: ['single', 'separate'],
      initial: 'single',
    };
  }

  return {
    hasSequential,
    hasSeparate,
    allowed: ['separate'],
    initial: 'separate',
  };
};

export const filterParticipantTypesForTrustOrLlcAdd = ({
  participantTypes = [],
  existingParticipants = [],
  resolveParticipantMeta,
}) =>
  participantTypes?.filter(
    (type) =>
      !isParticipantTypeBlockedForTrustOrLlcAdd(type, existingParticipants, resolveParticipantMeta)
  );

export const getTrustOrLlcExcludedParticipantTypes = ({
  participantTypes = [],
  existingParticipants = [],
  resolveParticipantMeta,
}) =>
  participantTypes?.filter((type) =>
    isParticipantTypeBlockedForTrustOrLlcAdd(type, existingParticipants, resolveParticipantMeta)
  );

export const formatTrustOrLlcExcludedParticipantTypesNote = (
  excludedTypes = [],
  fromOrderDetail = false
) => {
  if (!excludedTypes?.length) return '';
  const names = excludedTypes
    .map((type) => type?.participant_type_name)
    .filter(Boolean)
    .join(', ');
  if (!names) return '';
  return `Note: ${names} cannot be added because the primary participant is registered as a Trust or LLC entity. ${!fromOrderDetail ? 'Please add trustees or members instead.' : ''}`;
};

export const getTrustOrLlcEntityTypeValidationMessage = ({
  participantIndex,
  entityType,
  existingParticipants = [],
}) => {
  const normalizedEntityType = entityType?.toLowerCase?.()?.trim?.();
  if (normalizedEntityType !== 'trust' && normalizedEntityType !== 'llc') return '';

  const currentTypeId = existingParticipants?.[participantIndex]?.user_participant_type_id;
  if (!currentTypeId) return '';

  const primaryParticipantIndex = existingParticipants?.findIndex(
    (participant) => participant?.user_participant_type_id === currentTypeId
  );
  if (primaryParticipantIndex === -1 || primaryParticipantIndex === participantIndex) return '';

  return 'Primary participant already added. For Trust/LLC, add it as the primary participant and add other participants as members or trustees.';
};

export const getPrimaryParticipantEntityState = ({
  question,
  isBuyer = false,
  participantNo = 1,
  getFormValue,
  getFieldValue,
}) => {
  const sectionId = isBuyer ? BUYER_SECTION : SOI_SECTION_ID;
  const section = question?.sections?.find((sec) => sec?.section_id === sectionId);
  if (!section?.groups?.length) {
    return { entityValue: '', entityType: '' };
  }

  const individualEntityElement = findElement(section, IS_INDIVIDUAL_ENTITY);
  const entityValue =
    getFormValue?.(individualEntityElement?.element_id) ||
    individualEntityElement?.user_value ||
    getFieldValue?.(individualEntityElement, participantNo) ||
    '';

  const entityTypeElement = findElement(section, isBuyer ? BUYER_ENTITY_TYPE : ENTITY_TYPE);
  const entityType =
    getFormValue?.(entityTypeElement?.element_id) ||
    entityTypeElement?.user_value ||
    getFieldValue?.(entityTypeElement, participantNo) ||
    '';

  return { entityValue, entityType };
};

export const isPrimaryParticipantTrustOrLlcEntity = (params) => {
  const { entityValue, entityType } = getPrimaryParticipantEntityState(params);
  return entityValue === 'Entity' && (entityType === 'Trust' || entityType === 'LLC');
};

export const formatDateToYYYYMMDD = (date) => {
  // Extract the components of the date
  const year = date.getFullYear();
  // JavaScript months are 0-indexed, so add 1 to get the correct month
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Zero-padding month
  const day = String(date.getDate()).padStart(2, '0'); // Zero-padding day

  // Concatenate the components to form the desired format
  const formattedDate = `${year}-${month}-${day}`;

  return formattedDate;
};

export const constructLanguageJson = async (arr) => {
  try {
    const API_URL = 'https://translation.googleapis.com/language/translate/v2?key=';
    const languages = [...arr];
    const englishObj = {
      language: 'Language',
      languageMessage: `You can view the participant form in various available languages.`,
      propertyAddress: 'Property Address',
      orderDetails: 'Order Details',
      fileNumber: 'File Number',
      title: 'Title / Escrow Company',
      contact: 'Contact Information',
      note: 'Note : Your progress in previous sections is automatically saved. However, the details of the current section will not be saved automatically unless you click on the NEXT STEP button at the end of the section.',
      note1:
        'Note : The details of the current section will not be saved automatically unless you click on the NEXT STEP button at the end of the section.',
      notePOA: `Note: A power of attorney, also known as an “attorney-in-fact” is used in many real estate
            transactions to allow someone else to be able to complete closings or giving someone else
            the power to sell your properties.`,
      noteTrustLlcNoAdditionalParticipant: `Note: Because the primary participant is registered as a Trust or LLC entity, additional participants cannot be added here. Please use the Add Another Trustee/Member option in the entity section to include other individuals associated with this entity.`,
      noteAdditionalService: `Note : You can add an additional service and its relevant details if it is not listed
                here.`,
      addParticipant: 'Add Participant',
      addParticipantConfirmation: ` <b>Would you like to add an additional participant to this transaction?</b> You will be
              prompted to provide basic information <b>(Name, Email, Phone Number, SSN)</b> for any
              additional participants.`,

      serviceLabel: `Label For Service`,
      serviceLabelRequired: `Service Label is required`,
      addHOA: 'Add Another HOA',
      addLoan: 'Add Another Loan',
      addBuyer: 'Add Another Buyer',
      addBorrower: 'Add Another Borrower',
      noteHOA: `<b>Would you like to add an additional HOA for this transaction?</b> You will be
              prompted to provide basic information <b>(Association Name, Management Company, Association Dues)</b> for any
              additional HOA.`,
      noteLoan: `<b>Would you like to add an additional Loan for this transaction?</b> You will be
              prompted to provide basic information <b>(Lender Name, Loan Number, Approximate Balance)</b> for any
              additional Loans.`,

      noteBuyer: `<b>Would you like to add an additional buyer for this transaction?</b> You will be
              prompted to provide basic information <b>(Buyer Name, Email, Phone, DOB, etc.)</b> for any
              additional buyer.`,
      noteBorrower: `<b>Would you like to add an additional borrower for this transaction?</b> You will be
              prompted to provide basic information <b>(Name, Email, Phone Number, SSN)</b> for any
              additional borrower.`,
      lastSection: 'Signature & Documents',
      participantSignature: 'Participant Signature',
      signRequired: 'Please add signature to proceed further.',
      additionalSignature: `Additional Participant's Signature`,
      lastStepMessage1: `  These document(s) will be automatically signed electronically at the end.`,
      lastStepMessage2: `The form will NOT be submitted until ALL documents are reviewed and the submit
                    button below is clicked.`,

      congratulations: 'Congratulations',
      download: `DOWNLOAD FINAL FORM`,
      submissionMessage1: `Completed. Your documents have been successfully completed.`,
      submissionMessage2: `You are all set. We have sent an email to your spouse and/or any additional parties for their signatures. Once the signatures are complete, the documents will be forwarded to`,
      submissionMessage3: ` Secured and authenticated by ClientVerify California Inc ™`,
      somethingWrong: 'Something went wrong!',
      creditCardLabel: 'Credit Card Information',
      creditCardMessage: `By providing your card information, you allow localhost:3000 to charge your card for
                future payments in accordance with their terms.`,
      creditCardSuccess: `Credit card has been added successfully.`,
      pendingReview: 'Pending Review',
      docReviewed: 'You have reviewed this document.',
    };
    let convertedArr = [];
    let constructedJson = {};
    for (const key in englishObj) {
      convertedArr.push({ label: key, value: englishObj[key] });
    }
    let payloadForTranslate = convertedArr.map((val) => (val.value ? val.value : ''));
    for (const lng of languages) {
      const body = {
        q: payloadForTranslate,
        target: lng?.value,
      };
      const response = await axios.post(API_URL, body);
      const translations = response.data.data.translations.map(
        (translation) => translation.translatedText
      );

      let tempObj = {};
      const translatedCombinedArr = translations.map((translatedLabel, index) => ({
        label: convertedArr?.[index]?.label,
        value: translatedLabel,
      }));
      translatedCombinedArr.forEach((tra) => (tempObj[tra.label] = tra.value));
      constructedJson[lng?.value] = { translation: tempObj };
    }
    console.log(constructedJson);
    return constructedJson;
  } catch (e) {
    console.error(e);
  }
};

export const modifyResourceJson = (languages, key) => {
  let tempData = { ...resources };
  languages.forEach((language) => {
    if (tempData[language.value]) tempData[language.value].translation[key] = language.label;
  });
  return tempData;
};
// export const splitTexts = (texts, maxChars = 800) => {
//   let result = [];
//   let currentArray = [];
//   let currentLength = 0;

//   for (let text of texts) {
//     let textLength = text.length;
//     if (currentLength + textLength <= maxChars) {
//       currentArray.push(text);
//       currentLength += textLength;
//     } else {
//       result.push(currentArray);
//       currentArray = [text];
//       currentLength = textLength;
//     }
//   }

//   if (currentArray.length > 0) {
//     result.push(currentArray);
//   }

//   return result;
// };

export const findElements = (elements) => {
  let tempJson = cloneDeep(elements);
  let flatElements = [];
  let options = [];
  const answer = (elem) => {
    flatElements.push({ id: elem.element_id, label: elem.label });
    if (elem.options.length) {
      for (const option of elem?.options) {
        options.push({ id: option.option_id, label: option.option_name });
        for (const subElement of option?.elements) {
          answer(subElement);
        }
      }
    }
  };
  for (const elem of tempJson) {
    answer(elem);
  }
  return { elements: flatElements, options };
};
export const splitTexts = (inputArray) => {
  const result = [];
  for (let i = 0; i < inputArray.length; i += 30) {
    result.push(inputArray.slice(i, i + 30));
  }
  return result;
};
export const findAddressComponent = async (value) => {
  try {
    const results = await geocodeByAddress(value);
    const addressComponents = results[0].address_components;
    const streetAddress = addressComponents
      .filter((comp) => comp.types.includes('street_number') || comp.types.includes('route'))
      .map((comp) => comp.short_name)
      .join(' ');
    // Address description
    const city = addressComponents.find((comp) => comp.types.includes('locality'))?.long_name || '';
    const state =
      addressComponents.find((comp) => comp.types.includes('administrative_area_level_1'))
        ?.short_name || '';
    const zip =
      addressComponents.find((comp) => comp.types.includes('postal_code'))?.long_name || '';
    const fullAddress = streetAddress + ',' + city + ',' + state + ',' + zip;
    return {
      site_address: streetAddress,
      site_city: city,
      site_state: state,
      site_zip: zip,
      // site_unit: unit,
      site_full_address: fullAddress,
    };
  } catch (error) {
    console.error('Error selecting address:', error);
    return false;
  }
};
export const matchAddress = async (
  mobyAddress,
  userAddress,
  token,
  veriffAddressComponents = {}
) => {
  try {
    if (!mobyAddress && !Object.keys(veriffAddressComponents || {})?.length) return {};
    let mobyAddressDestructured = Object.keys(veriffAddressComponents || {})?.length
      ? veriffAddressComponents
      : await findAddressComponent(mobyAddress);
    if (!mobyAddressDestructured) return {};

    const addressVerificationObjMoby = await addressVerification(
      mobyAddressDestructured,
      token,
      true
    );
    if (!addressVerificationObjMoby?.success)
      return {
        [MAILING_ADDRESS_ADDRESS]: true,
        [MAILING_ADDRESS_STATE]: true,
        [MAILING_ADDRESS_CITY]: true,
        [MAILING_ADDRESS_ZIP]: true,
        state: false,
      };
    const verifiedMobyAddress = addressVerificationObjMoby?.data?.[0];
    if (!Object.keys(userAddress || {}).length)
      return {
        [MAILING_ADDRESS_ADDRESS]: true,
        [MAILING_ADDRESS_STATE]: true,
        [MAILING_ADDRESS_CITY]: true,
        [MAILING_ADDRESS_ZIP]: true,
        state: verifiedMobyAddress?.sa_site_state
          ? verifiedMobyAddress?.sa_site_state?.toUpperCase()
          : false,
        moby_address: mobyAddressDestructured,
      };
    const addressVerificationObjUser = await addressVerification(userAddress, token, true);
    if (!addressVerificationObjUser?.success)
      return {
        [MAILING_ADDRESS_ADDRESS]: true,
        [MAILING_ADDRESS_STATE]: true,
        [MAILING_ADDRESS_CITY]: true,
        [MAILING_ADDRESS_ZIP]: true,
        state: verifiedMobyAddress?.sa_site_state
          ? verifiedMobyAddress?.sa_site_state?.toUpperCase()
          : false,
        moby_address: mobyAddressDestructured,
      };
    const verifiedParticipantAddress = addressVerificationObjUser?.data?.[0];

    if (!verifiedMobyAddress || !verifiedParticipantAddress)
      return {
        [MAILING_ADDRESS_ADDRESS]: true,
        [MAILING_ADDRESS_STATE]: true,
        [MAILING_ADDRESS_CITY]: true,
        [MAILING_ADDRESS_ZIP]: true,
        state: verifiedMobyAddress?.sa_site_state
          ? verifiedMobyAddress?.sa_site_state?.toUpperCase()
          : false,
        moby_address: !verifiedMobyAddress ? {} : mobyAddressDestructured,
      };
    const isMultiple = addressVerificationObjUser?.multiple || addressVerificationObjMoby?.multiple;
    return {
      [MAILING_ADDRESS_ADDRESS]: isMultiple
        ? true
        : !(
            verifiedMobyAddress?.sa_site_street_name ===
              verifiedParticipantAddress?.sa_site_street_name &&
            verifiedMobyAddress?.sa_site_house_nbr === verifiedParticipantAddress?.sa_site_house_nbr
          ),
      [MAILING_ADDRESS_STATE]:
        verifiedMobyAddress?.sa_site_state !== verifiedParticipantAddress?.sa_site_state,
      [MAILING_ADDRESS_CITY]:
        verifiedMobyAddress?.sa_site_city !== verifiedParticipantAddress?.sa_site_city,
      [MAILING_ADDRESS_ZIP]:
        verifiedMobyAddress?.sa_site_zip !== verifiedParticipantAddress?.sa_site_zip,
      state: verifiedMobyAddress?.sa_site_state?.toUpperCase(),
      moby_address: mobyAddressDestructured,
      is_multiple: isMultiple,
    };
  } catch (error) {
    console.log(error.message);
    return {};
  }
};

export const maskEmail = (email) => {
  return email?.replace(/(^\w)(.*)(@\w+\.\w+)/, (match, firstChar, hiddenPart, domain) => {
    const masked = hiddenPart.replace(/[^.]/g, '*');
    return `${firstChar}${masked}${domain}`;
  });
};

export const snakeToTitleCase = (snakeStr) => {
  return snakeStr
    ?.split('_') // Split the string by underscores
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
    ?.join(' '); // Join words with a space
};

/** Entity member fields with member_private_field: member invite URL + parent_participant_number + user_sequence_number vs member_sequence_no. */
export const isMemberPrivateEntityField = (element) => {
  return element?.member_private_field === true && element?.is_entity_member_field === 'yes';
};

export const isMemberPrivateFieldVisibleToSession = (
  authUser,
  element,
  routeMemberUUID,
  routeParticipantNo
) => {
  if (!isMemberPrivateEntityField(element)) return true;
  if (!routeMemberUUID) return false;
  if (!authUser?.token) return false;

  const routeN = Number(routeParticipantNo);
  const routePart = Number.isFinite(routeN) && routeN > 0 ? routeN : 1;
  const parentN = Number(element?.parent_participant_number);
  const fieldParent = Number.isFinite(parentN) && parentN > 0 ? parentN : 1;
  if (fieldParent !== routePart) return false;

  const fieldSeq = Number(element?.member_sequence_no ?? 1);
  const userSeq = Number(authUser?.user_sequence_number);
  if (!Number.isFinite(userSeq)) return false;
  return userSeq === fieldSeq;
};

/** DL number field for entity member/trustee (not the state select). */
export const isEntityMemberDriverLicenseNumberElement = (element) => {
  const n = element?.element_name || '';
  return (
    element?.is_entity_member_field === 'yes' &&
    n?.includes(ENTITY_MEMBER_DL_LICENSE_INFIX) &&
    !n?.includes(ENTITY_MEMBER_DL_STATE_INFIX)
  );
};

export const isEntityMemberDriverLicenseStateElement = (element) => {
  const n = element?.element_name || '';
  return element?.is_entity_member_field === 'yes' && n?.includes(ENTITY_MEMBER_DL_STATE_INFIX);
};

export const isEntityMemberDriverLicenseDobElement = (element) => {
  const n = element?.element_name || '';
  return element?.is_entity_member_field === 'yes' && n?.includes(ENTITY_MEMBER_DL_DOB_INFIX);
};

export const isEntityMemberSsnElement = (element) => {
  const n = element?.element_name || '';
  return element?.is_entity_member_field === 'yes' && n?.includes(ENTITY_MEMBER_SSN_INFIX);
};

/** Scroll the step form panel only (not the document) to a field by data-field-id. */
export const scrollFieldIntoContainer = (
  elementId,
  scrollContainerSelector = '.step-details-scroll',
  scrollOffset = 24
) => {
  if (!elementId || typeof document === 'undefined') return false;

  const el = document.querySelector(`[data-field-id="${CSS.escape(elementId)}"]`);
  if (!el) return false;

  const scrollContainer =
    el.closest(scrollContainerSelector) || document.querySelector(scrollContainerSelector);
  if (!scrollContainer) return false;

  const nextScrollTop =
    scrollContainer.scrollTop +
    (el.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top) -
    scrollOffset;

  scrollContainer.scrollTo({
    top: Math.max(0, nextScrollTop),
    behavior: 'smooth',
  });

  return true;
};

export const isMemberPrivateSaveFieldElement = (element) =>
  isEntityMemberSsnElement(element) ||
  isEntityMemberDriverLicenseNumberElement(element) ||
  isEntityMemberDriverLicenseStateElement(element) ||
  isEntityMemberDriverLicenseDobElement(element);

export const isMemberSsnComplete = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 9;
};


/** Entity member invite: only fields for this member (sequence + private session) are editable. */
export const isEntityMemberEditableField = (
  authUser,
  element,
  routeMemberUUID,
  routeParticipantNo
) => {
  if (!routeMemberUUID || !element) return true;
  if (element?.is_entity_member_field !== 'yes') return false;

  const fieldSeq = Number(element?.member_sequence_no ?? 1);
  const userSeq = Number(authUser?.user_sequence_number);
  if (!Number.isFinite(userSeq) || userSeq !== fieldSeq) return false;

  const routeN = Number(routeParticipantNo);
  const routePart = Number.isFinite(routeN) && routeN > 0 ? routeN : 1;
  const parentN = Number(element?.parent_participant_number);
  const fieldParent = Number.isFinite(parentN) && parentN > 0 ? parentN : 1;

  return fieldParent === routePart; 
};

export const shouldSkipValidationForMemberPrivateField = (
  authUser,
  element,
  routeMemberUUID,
  routeParticipantNo
) => {
  if (!isMemberPrivateEntityField(element)) return false;
  return !isMemberPrivateFieldVisibleToSession(
    authUser,
    element,
    routeMemberUUID,
    routeParticipantNo
  );
};

const ENTITY_MEMBER_PHONE_INFIX = '_entity_member_phone_';

/** `start` | `end` for member-login editable zone dividers (label / prev phone / own DOB). */
export const getEntityMemberZoneDividerBoundary = (
  authUser,
  element,
  routeMemberUUID,
  routeParticipantNo,
  dividerKind
) => {
  if (!routeMemberUUID || !element) return null;
  const userSeq = Number(authUser?.user_sequence_number);
  if (!Number.isFinite(userSeq)) return null;
  const routePart = Number(routeParticipantNo);
  const routeParticipant = Number.isFinite(routePart) && routePart > 0 ? routePart : 1;
  const fieldParent = Number(
    element?.parent_participant_number ||
      element?.participant_number ||
      element?.participant_no ||
      1
  );
  if (fieldParent !== routeParticipant) return null;

  const fieldMemberSeq = Number(element?.member_sequence_no ?? 1);
  const name = element?.element_name || '';

  if (dividerKind === 'label') {
    if (userSeq !== 1) return null;
    if (!name.includes(ENTITY_TRUSTEE_MEMBERS_LABEL)) return null;
    return 'start';
  }
  if (dividerKind === 'phone-divider') {
    if (userSeq <= 1) return null;
    if (!name.includes(ENTITY_MEMBER_PHONE_INFIX)) return null;
    if (fieldMemberSeq !== userSeq - 1) return null;
    return 'start';
  }
  if (dividerKind === 'dob-divider') {
    if (!name.includes(ENTITY_MEMBER_DL_DOB_INFIX)) return null;
    if (fieldMemberSeq !== userSeq) return null;
    return 'end';
  }
  return null;
};

// Spouse group is visible only when seller is Married and no additional seller is their spouse.
// Keep in sync with showSpouseGroup in StepDetails.jsx.
export const isSpouseGroupVisible = (question, getFormValue = () => undefined) => {
  const soiSection = question?.sections?.find((sec) => sec?.section_id === SOI_SECTION_ID);
  if (!soiSection) return false;
  const readValue = (el) =>
    (el?.element_id && (getFormValue(el.element_id) || getFieldValue(el, 1))) || '';
  if (readValue(findElementByName(soiSection, SELLER_MARITAL_STATUS_FIELD)) !== 'Married')
    return false;

  const additionalSellerElement = findElement(soiSection, ADDITIONAL_SELLER_PARENT_ID);
  if (readValue(additionalSellerElement) !== 'Yes') return true;

  const isSpouseElements = [];
  const collect = (el) => {
    if (el?.element_name?.startsWith(SELLER_IS_SPOUSE_FIELD_PREFIX)) isSpouseElements.push(el);
    el?.options?.forEach((opt) => opt?.elements?.forEach(collect));
  };
  soiSection.groups?.forEach((grp) => grp?.elements?.forEach(collect));
  return !isSpouseElements.some((el) => readValue(el) === 'Yes');
};

export const checkFormFieldError = async (
  question,
  isAdditionalSeller,
  participantNo,
  getDocument = () => {},
  participants = [],
  token = '',
  memberPrivateContext = null,
  getFormValue = undefined
) => {
  let tempJson = cloneDeep(question);
  let errorSections = [];
  const showSpouseGroup = isSpouseGroupVisible(question, getFormValue);
  const answer = async (elem, section) => {
    const participantNumber = elem?.participant_no || elem?.seller_no || 1;

    if (
      !isMemberPrivateEntityField(elem) &&
      (elem.element_type === DocElementType.TextField ||
        elem.element_type === DocElementType.TextArea ||
        elem.element_type === DocElementType.SelectBox ||
        elem.element_type === DocElementType.RadioButton ||
        elem.element_type === DocElementType.SocialNumber ||
        elem.element_type === DocElementType.SSelectBox ||
        elem.element_type === DocElementType.DateField ||
        elem.element_type === DocElementType.Checkbox) &&
      elem.display &&
      !elem.disabled &&
      !SSN_FIELDS.find((field) => elem?.element_name?.startsWith(field)) &&
      (elem?.is_private ? participantNumber == participantNo : true)
    ) {
      let valueToCheck = getFieldValue(elem, participantNo);
      if (elem?.is_user_value_encrypted === 'yes' && valueToCheck?.length > 30 && token) {
        const res = await getDecryptedBankAcc({ encrypted_string: valueToCheck || '' }, token);
        if (res?.success && res?.response?.decrypted_value) {
          valueToCheck = res?.response?.decrypted_value;
        }
      }

      const error = errorChecker(elem, valueToCheck);

      let ignoreRequireValidation = false;

      if (startsWithAny(elem.element_name, FIELDS_CHECK_VALIDATIONS)) {
        const splits = elem.element_name.split('_');
        const loanNum = splits[splits?.length - 1];
        const docData = getDocument(LOAN_UPLOAD_NAME + `_${loanNum}`);
        if (docData?.[0]?.file_name?.length) {
          ignoreRequireValidation = true;
        }
      }
      if (error?.isError && !ignoreRequireValidation) {
        const isAvailable = errorSections?.find((sec) => sec?.section_id === section.section_id);
        if (!isAvailable) {
          errorSections.push(section);
        }
      }
    }
    if (elem.options.length) {
      for (const option of elem?.options) {
        if (
          getFieldValue(elem, participantNo) &&
          getFieldValue(elem, participantNo) === option.option_name
        ) {
          for (const subElement of option?.elements) {
            if (!isAdditionalSeller && subElement?.participant_no && subElement?.participant_no > 1)
              continue;
            if (subElement?.participant_no && subElement?.participant_no != participantNo) continue;
            await answer(subElement, section);
          }
        }
      }
    }
  };
  for (const section of tempJson?.sections) {
    if (section?.is_hidden) continue;
    for (const group of section?.groups) {
      if (group.is_hidden) continue;
      if (group?.group_id === SELLER_SHARED_GROUP_ID && participants?.length < 2) {
        continue;
      }
      if (group?.group_id === SELLER_SPOUSE_GROUP_ID && !showSpouseGroup) continue;

      for (const element of group.elements) {
        if (!isAdditionalSeller && element?.participant_no && element?.participant_no > 1) {
          continue;
        }
        if (element?.participant_no && element?.participant_no != participantNo) {
          continue;
        }
        await answer(element, section);
      }
    }
  }
  return errorSections;
};

export const updateSourceData = (que, sectionIdx, sourceData, reasonCodes) => {
  let elemData = cloneDeep(que);
  const answer = (element) => {
    if (sourceData[element?.element_id]) {
      element.value_from_bank_source = sourceData?.[element?.element_id];
      element.source_name = 'Sardine';
      element.reason_code_desc = reasonCodes;
    }
    if (element.options.length) {
      for (const option of element?.options) {
        for (const subElement of option?.elements) {
          answer(subElement);
        }
      }
    }
  };
  for (const grp of elemData?.sections?.[sectionIdx]?.groups) {
    for (const element of grp?.elements) {
      answer(element);
    }
  }
  return elemData;
};

export const findParentElement = (group, element_id) => {
  if (!group?.elements?.length) return null;

  let parentElem = null;
  let optionId = null;

  const search = (element, parent, option_id) => {
    // If the element is the one we are looking for, return the parent
    if (element?.element_id == element_id) {
      parentElem = parent;
      optionId = option_id;
      return;
    }

    // Traverse the options and their elements if available
    if (element.options?.length) {
      for (const option of element?.options) {
        for (const subElement of option?.elements) {
          search(subElement, element, option?.option_id); // Pass current element as the parent
          if (parentElem) return; // Early return if parent is found
        }
      }
    }
  };

  // Traverse each group and its elements
  for (const element of group?.elements) {
    search(element, null, null); // Null since top-level elements don't have a parent
    if (parentElem) return { parent: parentElem, optionId };
  }

  return { parent: parentElem, optionId }; // Return the parent element or null if not found
};

export const findSectionAndGroupForElement = (sections, elementId) => {
  let result = {
    section: null,
    group: null,
  };

  // Recursive helper function to search for the element within options and elements
  const searchElement = (element, section, group) => {
    // If the element matches the given elementId, store the current section and group
    if (element?.element_id === elementId) {
      result = { section, group };
      return true;
    }

    // Traverse the options and their elements if they exist
    if (element?.options?.length) {
      for (const option of element.options) {
        for (const subElement of option?.elements || []) {
          if (searchElement(subElement, section, group)) {
            return true; // Stop further traversal if the element is found
          }
        }
      }
    }

    return false;
  };

  // Traverse each section and its groups
  for (const section of sections) {
    for (const group of section?.groups || []) {
      for (const element of group?.elements || []) {
        if (searchElement(element, section, group)) {
          return result; // Return the result if the element is found
        }
      }
    }
  }

  return result; // Return null if no matching element is found
};

export const startsWithAny = (input, prefixes) => {
  return prefixes.some((prefix) => input.startsWith(prefix));
};

export const checkAvailable = (elements, participantNo) => {
  let isAvailable = false;
  const answer = async (element) => {
    const elemNo = element?.participant_no || 1;
    if (element?.display && (element?.is_private ? +elemNo === +participantNo : true))
      isAvailable = true;

    if (element.options.length) {
      for (const option of element.options) {
        for (const subElement of option.elements) {
          if (isAvailable) {
            break;
          }
          await answer(subElement);
        }
      }
    }
  };
  for (const element of elements) {
    if (isAvailable) {
      break;
    }
    answer(element);
  }
  return isAvailable;
};

export const getFieldValue = (element, participantNo = 1, fromAdmin = false) => {
  return fromAdmin
    ? element?.[`user_value_${participantNo}`] || element?.user_value
    : element?.[`user_value_${participantNo}`] ||
        element?.[`user_value_${element?.added_by}`] ||
        element?.user_value;
};

export const cleanStringForComparison = (str) => {
  if (!str || typeof str !== 'string') return '';

  return str
    .toLowerCase()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

export const compareStrings = (str1, str2) => {
  return cleanStringForComparison(str1) === cleanStringForComparison(str2);
};

/**
 * Name comparison that can also return per-token match info for highlighting.
 * - Normalizes both sides (same as compareStrings).
 * - If token counts differ, falls back to whole-string compare and returns tokenMatches = null.
 * - If token counts match, compares each token and returns an array of booleans.
 */
export const compareNamesWithTokenHighlights = (left, right) => {
  const leftClean = cleanStringForComparison(left);
  const rightClean = cleanStringForComparison(right);

  if (!leftClean && !rightClean) {
    return { isMatch: true, tokenMatches: null };
  }

  const leftTokens = leftClean.split(' ');
  const rightTokens = rightClean.split(' ');

  if (leftTokens?.length !== rightTokens?.length) {
    return { isMatch: leftClean === rightClean, tokenMatches: null };
  }

  const tokenMatches = leftTokens?.map((token, idx) => token === rightTokens?.[idx]);
  const isMatch = tokenMatches?.every(Boolean);
  return { isMatch, tokenMatches };
};

/**
 * Use with compareNamesWithTokenHighlights:
 * - If tokenMatches is null, falls back to plain formatSummaryNameDisplay(value).
 * - Otherwise, uppercases the value and wraps mismatched tokens in a red <span>.
 */
export const formatSummaryNameDisplayWithHighlights = (value, tokenMatches) => {
  const base = formatSummaryNameDisplay(value);
  if (!tokenMatches || !tokenMatches?.length) return base;

  const tokens = base.split(' ');
  if (tokens?.length !== tokenMatches?.length) return base;

  const highlightedTokens = tokens.map((tok, idx) => {
    if (tokenMatches?.[idx]) return tok;
    return `<span style="color:#c62828; font-weight:600;">${tok}</span>`;
  });

  return highlightedTokens.join(' ');
};

/**
 * First–middle–last comparable string for DL / bank (order: first, middle, last).
 * @param {string} nameMatchingMode - Pass from screen-level config (e.g. CURRENT_NAME_MATCHING_MODE).
 */
export const getFormattedName = (firstName, lastName, middleName = null, nameMatchingMode) => {
  const formatName = (name) => {
    if (!name) return '';
    return String(name)
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };
  const first = formatName(firstName);
  const middle = formatName(middleName);
  const last = formatName(lastName);

  if (nameMatchingMode === 'strict_name_validation') {
    return `${first}${middle ? ` ${middle}` : ''}${last ? ` ${last}` : ''}`.trim();
  }
  if (nameMatchingMode === 'match_middle_name_initial') {
    const middleInitial = middle ? middle.charAt(0) : '';
    return `${first}${middleInitial ? ` ${middleInitial}` : ''}${last ? ` ${last}` : ''}`.trim();
  }
  if (nameMatchingMode === 'ignore_middle_name') {
    return `${first}${last ? ` ${last}` : ''}`.trim();
  }
  return `${first}${middle ? ` ${middle}` : ''}${last ? ` ${last}` : ''}`.trim();
};

/** Bank name-on-account user_value is "first,middle,last" CSV — same shape as getFormattedName output. */
export const getComparableNameFromBankUserValue = (userValue, nameMatchingMode) => {
  if (!userValue || typeof userValue !== 'string') return '';
  const parts = userValue.split(',');
  if (parts.length >= 3) {
    return getFormattedName(
      parts[0]?.trim(),
      parts[2]?.trim(),
      parts[1]?.trim() || null,
      nameMatchingMode
    );
  }
  if (parts.length === 2) {
    return getFormattedName(parts[0]?.trim(), parts[1]?.trim(), null, nameMatchingMode);
  }
  return '';
};

/**
 * Comparable name-on-account value for bank verification summaries.
 * - Entity name-on-account is a single business-name string — compared as-is (whole string),
 *   never reformatted by name-matching config (first/middle/last rules don't apply to it).
 * - Individual (and legacy) name-on-account is "first,middle,last" CSV — parsed as before.
 */
export const getComparableBankAccountName = (element, nameMatchingMode) => {
  if (element?.is_entity_bank_account_name_field) {
    return `${element?.user_value || ''}`.trim();
  }
  return getComparableNameFromBankUserValue(element?.user_value, nameMatchingMode);
};

/**
 * Freeform "First [Middle...] Last" by token count: 1 = FN only; 2 = FN LN; 3+ = FN + middle + LN.
 */
export const splitFullNameIntoFnMnLn = (raw) => {
  const s = String(raw ?? '').trim();
  const parts = s ? s?.split(/\s+/).filter(Boolean) : [];
  const n = parts?.length;
  const fn = n ? parts[0] : '';
  const ln = n > 1 ? parts[n - 1] : '';
  const mn = n > 2 ? parts?.slice(1, -1).join(' ') || '' : '';
  return { fn, mn, ln };
};

/**
 * participant_name_on_title_toolbox: last name, first name, middle (comma- or space-separated).
 * Middle for space input is all tokens after the second word.
 */
export const parseParticipantNameOnTitleToolbox = (titleValue) => {
  if (!titleValue || typeof titleValue !== 'string') {
    return { lastName: '', firstName: '', middleName: null };
  }
  const trimmed = titleValue.trim();
  if (!trimmed) return { lastName: '', firstName: '', middleName: null };

  if (trimmed.includes(',')) {
    const parts = trimmed
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (parts.length === 0) return { lastName: '', firstName: '', middleName: null };
    if (parts.length === 1) return { lastName: parts[0], firstName: '', middleName: null };
    if (parts.length === 2) return { lastName: parts[0], firstName: parts[1], middleName: null };
    return {
      lastName: parts[0],
      firstName: parts[1],
      middleName: parts.slice(2).join(' ').trim() || null,
    };
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { lastName: '', firstName: '', middleName: null };
  if (parts.length === 1) return { lastName: parts[0], firstName: '', middleName: null };
  if (parts.length === 2) return { lastName: parts[0], firstName: parts[1], middleName: null };
  return {
    lastName: parts[0],
    firstName: parts[1],
    middleName: parts.slice(2).join(' ').trim() || null,
  };
};

/** Same rules as getFormattedName but output order matches title toolbox: Last First Middle. */
export const buildComparableNameLastFirstMiddle = (
  lastName,
  firstName,
  middleName = null,
  nameMatchingMode
) => {
  const formatName = (name) => {
    if (!name) return '';
    return String(name)
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };
  const first = formatName(firstName);
  const middle = formatName(middleName);
  const last = formatName(lastName);

  if (nameMatchingMode === 'strict_name_validation') {
    return `${last}${first ? ` ${first}` : ''}${middle ? ` ${middle}` : ''}`.trim();
  }
  if (nameMatchingMode === 'match_middle_name_initial') {
    const middleInitial = middle ? middle.charAt(0) : '';
    return `${last}${first ? ` ${first}` : ''}${middleInitial ? ` ${middleInitial}` : ''}`.trim();
  }
  if (nameMatchingMode === 'ignore_middle_name') {
    return `${last}${first ? ` ${first}` : ''}`.trim();
  }
  return `${last}${first ? ` ${first}` : ''}${middle ? ` ${middle}` : ''}`.trim();
};

/** Split one participant's summary rows into parent-level vs entity member sub-groups. */
export const splitSummaryItemsBySubParticipant = (items = []) => {
  const parentItems = [];
  const memberMap = {};
  for (const item of items) {
    const sub = item?.sub_participant_number;
    if (sub != null && sub !== '') {
      const key = String(sub);
      if (!memberMap[key]) memberMap[key] = [];
      memberMap[key].push(item);
    } else {
      parentItems.push(item);
    }
  }
  const memberSubGroups = Object.keys(memberMap)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => ({
      subParticipantNum: Number(key) || key,
      items: memberMap[key],
    }));
  return { parentItems, memberSubGroups };
};

/** Title-case each word for summary UI (name mismatch rows). Matches inline getFormattedName casing. */
export const formatSummaryNameDisplay = (value) => {
  if (value === undefined || value === null) return '';
  const s = (typeof value === 'string' ? value : String(value)).trim();
  if (!s) return '';
  return s?.toUpperCase();
};

export const cleanErrorMessage = (htmlString) => {
  if (!htmlString) return '';
  return htmlString
    .replace(/<li>/g, '')
    .replace(/<\/li>/g, '')
    .replace(/<ul>/g, '')
    .replace(/<\/ul>/g, '')
    .replace(/<br\s*\/?>/g, '\n');
  // .replace(/<[^>]*>/g, ''); // Remove any remaining HTML tags
};

const splitFirstMiddle = (person) => {
  if (person?.nameComponents?.firstNameOnly && person?.nameComponents?.middleName) {
    return {
      firstName: person?.nameComponents?.firstNameOnly,
      middleName: person?.nameComponents?.middleName,
    };
  }
  const fullName = person?.firstName;
  if (!fullName || typeof fullName !== 'string') {
    return { firstName: '', middleName: '' };
  }

  const parts = fullName.trim().split(/\s+/); // split by space(s)

  if (parts.length === 1) {
    return { firstName: parts[0], middleName: '' };
  }

  const firstName = parts[0];
  const middleName = parts.slice(1).join(' '); // combine rest as middle name

  return { firstName, middleName };
};

export const isDlReuploadAllowed = (quota) => quota?.allowed === true;

export const getVeriffPendingSessionUrl = (response) =>
  response?.session_url || response?.invitation_url || '';

export const getVeriffSessionCreateFlags = (response, defaults = {}) => ({
  is_reopened_section:
    response?.is_reopened_section === 'yes'
      ? 'yes'
      : defaults.is_reopened_section === 'yes'
        ? 'yes'
        : 'no',
  is_reuploaded_document:
    response?.is_reuploaded_document === 'yes'
      ? 'yes'
      : defaults.is_reuploaded_document === 'yes'
        ? 'yes'
        : 'no',
});

export const shouldResumePendingVeriffSession = (response) => {
  if (!response || getVeriffPendingSessionUrl(response)) {
    return false;
  }

  return (
    response?.is_reuploaded_document === 'yes' ||
    (response?.is_reopened_section === 'yes' && response?.status === 'pending')
  );
};

export const convertNewToOldVeriffDetails = (newResponse) => {
  const reuploadQuota = newResponse?.response?.reupload_quota;
  const responseWithoutQuota = { ...(newResponse?.response || {}) };
  delete responseWithoutQuota.reupload_quota;
  if (!Object.keys(responseWithoutQuota)?.length) {
    return {
      ...newResponse,
      response: reuploadQuota ? { reupload_quota: reuploadQuota } : newResponse?.response || {},
    };
  }

  // If already old format, return as-is
  if (
    newResponse &&
    newResponse?.response &&
    newResponse?.response?.transaction_response &&
    newResponse?.response?.transaction_id
  ) {
    return {
      ...newResponse,
      response: {
        ...newResponse.response,
        reupload_quota: reuploadQuota || newResponse.response?.reupload_quota || null,
        dl_images: [
          { url: newResponse?.response?.dl_image_1, name: 'document-front' },
          { url: newResponse?.response?.dl_image_2, name: 'document-back' },
        ]?.filter((image) => image?.url?.length),
      },
    };
  }

  const session =
    newResponse && newResponse.response && newResponse.response.session_decision_response
      ? newResponse.response.session_decision_response
      : {};
  const verification = session.verification ? session.verification : {};
  const person = verification.person ? verification.person : {};
  const document = verification.document ? verification.document : {};
  const technicalData = session.technicalData ? session.technicalData : {};

  // Derived field
  const isDocExpired = document.validUntil ? new Date(document.validUntil) < new Date() : false;

  // Split first and middle name
  const { firstName, middleName } = splitFirstMiddle(person);
  const address = person?.addresses?.[0];
  // Map old fields safely with ternaries
  const mapped = {
    Id: session.integration_log_id ? session.integration_log_id : '',
    addressComponents: address
      ? {
          site_address:
            (address?.parsedAddress?.houseNumber ? `${address?.parsedAddress?.houseNumber} ` : '') +
            address?.parsedAddress?.street,
          site_city: address?.parsedAddress?.city,
          site_state: address?.parsedAddress?.state,
          site_zip: address?.parsedAddress?.postcode,
          site_full_address: address?.fullAddress,
        }
      : {},
    Csid: verification.id
      ? verification.id + '/' + (session.integration_log_id ? session.integration_log_id : '')
      : '',
    FirstName: firstName ? firstName : '',
    SecondName: middleName ? middleName : '',
    FirstSurname: person.lastName ? person.lastName : '',
    SecondSurname: '',
    IdentificationNumber: document.number
      ? document.number
      : person.idNumber
        ? person.idNumber
        : '',
    Approved: verification.status ? verification.status === 'approved' : false,
    NotApproved: verification.status ? verification.status !== 'approved' : false,
    Pending: verification.status ? verification.status === 'pending' : false,
    ErrorMessage: verification.reason ? verification.reason : '',
    IdentificationTypeId: document.type ? ID_MAPPING_VERIFF?.[document.type] : '',
    IdentificationType: document.type ? document.type : '',
    BirthDate: person.dateOfBirth ? person.dateOfBirth : '',
    ExpirationDate: document.validUntil ? document.validUntil : '',
    DocExpired: isDocExpired ? true : false,
  };

  // Additional fields from new response
  const flatNew = {
    session_status: session.status ? session.status : '',
    verification_status: verification.status ? verification.status : '',
    verification_id: verification.id ? verification.id : '',
    attemptId: verification.attemptId ? verification.attemptId : '',
    endUserId: verification.endUserId ? verification.endUserId : '',
    reasonCode: verification.reasonCode ? verification.reasonCode : '',
    vendorData: verification.vendorData ? verification.vendorData : '',
    decisionTime: verification.decisionTime ? verification.decisionTime : '',
    acceptanceTime: verification.acceptanceTime ? verification.acceptanceTime : '',
    country: document.country ? document.country : '',
    state: document.state ? document.state : '',
    validFrom: document.validFrom ? document.validFrom : '',
    ip: technicalData.ip ? technicalData.ip : '',
  };

  const transaction_response = { ...flatNew, ...mapped };

  return {
    success: newResponse && newResponse.success ? newResponse.success : false,
    message: newResponse && newResponse.message ? newResponse.message : '',
    response: {
      reupload_quota: reuploadQuota || null,
      dl_address_match_with_mailing_address: newResponse?.response
        ?.dl_address_match_with_mailing_address
        ? newResponse?.response?.dl_address_match_with_mailing_address
        : '',
      transaction_response: transaction_response,
      status:
        newResponse && newResponse.response && newResponse.response.status
          ? newResponse.response.status
          : '',
      transaction_id:
        newResponse && newResponse.response && newResponse.response.session_id
          ? newResponse.response.session_id
          : '',
      manual_verification_status:
        newResponse && newResponse.response && newResponse.response.manual_verification_status
          ? newResponse.response.manual_verification_status
          : '',
      ofac_check_result: newResponse?.response?.ofac_check_result
        ? newResponse?.response?.ofac_check_result
        : {},
      dl_images: newResponse?.response?.dl_images?.length
        ? newResponse?.response?.dl_images
        : [
            { url: newResponse?.response?.dl_image_1, name: 'document-front' },
            { url: newResponse?.response?.dl_image_2, name: 'document-back' },
          ]?.filter((image) => image?.url?.length),
    },
  };
};

export const docImageNameGenerator = (govermentID, imageName) => {
  if (!imageName?.length) return govermentID;
  if (imageName === 'face') return 'Face Selfie';
  if (imageName === 'document-front') return `${govermentID} [Front]`;
  if (imageName === 'document-back') return `${govermentID} [Back]`;
};

export const scrollToFirstVisibleError = (
  errorDetails,
  options = { behavior: 'smooth', block: 'center' },
  scrollContainerSelector = '.step-details-scroll'
) => {
  if (!errorDetails || typeof errorDetails !== 'object') return false;

  try {
    const scrollBehavior =
      options && typeof options === 'object' && options.behavior ? options.behavior : 'smooth';
    const scrollBlock =
      options && typeof options === 'object' && options.block ? options.block : 'center';
    const scrollOptions = { behavior: scrollBehavior, block: scrollBlock };

    for (const [id, info] of Object.entries(errorDetails)) {
      if (!info?.error) continue;
      const escapedId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id;
      const selector = `[data-field-id="${escapedId}"]`;
      const el = document.querySelector(selector);
      if (!el) continue;

      const container = scrollContainerSelector
        ? document.querySelector(scrollContainerSelector)
        : null;

      if (container && !container.contains(el)) continue;

      if (container) {
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const currentScrollTop = container.scrollTop;
        const elementTop = elRect.top - containerRect.top + currentScrollTop;
        let targetTop = elementTop;

        if (scrollBlock === 'center') {
          targetTop = elementTop - container.clientHeight / 2 + elRect.height / 2;
        } else if (scrollBlock === 'end') {
          targetTop = elementTop - container.clientHeight + elRect.height;
        }

        container.scrollTo({
          top: Math.max(targetTop, 0),
          behavior: scrollBehavior,
        });
      } else {
        el.scrollIntoView(scrollOptions);
      }

      const focusable = el.querySelector(
        'input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable && typeof focusable.focus === 'function') {
        setTimeout(() => {
          focusable.focus();
        }, 300);
      }
      return true;
    }
  } catch (error) {
    console.error('Error while finding first visible error element:', error);
  }

  return false;
};

export const stripHtmlTags = (text) => {
  if (!text || typeof text !== 'string') return text;
  // Remove HTML tags: <i>, </i>, <b>, </b>, <strong>, </strong>
  return text.replace(/<\/?(i|b|strong)[^>]*>/gi, '');
};

export const isUnauthenticatedResponse = (response) => {
  try {
    if (!response || typeof response !== 'object') return false;

    if (response.status === 401) return true;

    const responseData = response?.data ?? response;
    if (!responseData || typeof responseData !== 'object') return false;

    if (responseData?.status === 401) return true;

    if (responseData?.success === true) return false;

    const message = responseData?.message;
    if (!message || typeof message !== 'string') return false;

    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('unauthenticated') ||
      lowerMessage.includes('session expired') ||
      lowerMessage.includes('token expired') ||
      lowerMessage.includes('unauthorized')
    );
  } catch (error) {
    console.warn('Error checking unauthenticated response:', error);
    return false;
  }
};

let lastSessionExpiredTrigger = 0;
const SESSION_EXPIRED_DEBOUNCE_MS = 2000;

/**
 * Dispatches a custom event to trigger session expired modal
 * Includes debouncing to prevent multiple popups
 */
export const triggerSessionExpired = () => {
  try {
    const now = Date.now();
    if (now - lastSessionExpiredTrigger < SESSION_EXPIRED_DEBOUNCE_MS) {
      return;
    }

    lastSessionExpiredTrigger = now;
    const event = new CustomEvent('sessionExpired', {
      detail: { timestamp: now },
    });
    window.dispatchEvent(event);
  } catch (error) {
    console.warn('Error triggering session expired event:', error);
  }
};

export const generateLienData = (sellerData) => {
  if (!sellerData || typeof sellerData !== 'object') {
    return {
      ownerInvoluntaryLiens: { count: 0, liens: [], message: null },
      aliasOtherInvoluntaryLiens: { count: 0, liens: [], message: null },
      currentUnreleasedDeeds: { count: 0, deeds: [], message: null },
    };
  }

  const lienData = Array.isArray(sellerData?.LIEN) ? sellerData?.LIEN : [];

  if (!lienData?.length) {
    return {
      ownerInvoluntaryLiens: { count: 0, liens: [], message: null },
      aliasOtherInvoluntaryLiens: { count: 0, liens: [], message: null },
      currentUnreleasedDeeds: { count: 0, deeds: [], message: null },
    };
  }

  const dateDash2Slash = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === '') {
      return '';
    }
    try {
      const parts = String(dateString).split('-');
      return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : dateString;
    } catch {
      return '';
    }
  };

  const showCurrency = (amount) => {
    if (amount == null || amount === '' || amount === 0) {
      return '';
    }
    try {
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
      return !isNaN(numAmount) && numAmount !== 0
        ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(numAmount)
        : '';
    } catch {
      return '';
    }
  };

  const getLienholder = (lienItem) => {
    if (!lienItem || typeof lienItem !== 'object') return '';
    const lienType = lienItem?.lien_type || '';
    const bankruptcyChapter = lienItem?.bankruptcy_chapter || '';
    const lienIssuingAgency = lienItem?.lien_issuing_agency || '';
    const creditorName = lienItem?.creditor_name || '';

    return lienType && lienType?.toLowerCase()?.includes('bankruptcy')
      ? `${lienType}${bankruptcyChapter ? ` CH ${bankruptcyChapter}` : ''}`
      : lienIssuingAgency || creditorName || lienType || '';
  };

  const getBorrowerDebtor = (lienItem) => {
    if (!lienItem || typeof lienItem !== 'object') return '';
    const firstName = lienItem?.first_name_or_company_name || '';
    const lastName = lienItem?.last_name || '';
    return lastName && lastName !== '' && lastName !== null
      ? `${firstName} ${lastName}`.trim()
      : firstName || '';
  };

  const getStatus = (lienItem) => {
    if (!lienItem?.released || typeof lienItem.released !== 'object') return '';
    const released = lienItem.released;
    return released?.date &&
      released.date !== '0000-00-00' &&
      released.date !== '' &&
      released?.document_number &&
      released.document_number !== ''
      ? 'RELEASED'
      : '';
  };

  let countJudgements = 0;
  if (Array.isArray(lienData) && lienData.length > 0) {
    lienData.forEach((owner) => {
      if (owner?.judgements && Array.isArray(owner.judgements)) {
        countJudgements += owner.judgements.length;
      }
    });
  }

  const ownerLiens = [];
  if (Array.isArray(lienData) && lienData.length > 0 && countJudgements > 0) {
    let sr = 1;
    lienData.forEach((owner) => {
      if (
        owner &&
        typeof owner === 'object' &&
        owner?.judgements &&
        Array.isArray(owner.judgements) &&
        owner.judgements.length > 0
      ) {
        owner.judgements.forEach((lienItem) => {
          if (lienItem && typeof lienItem === 'object') {
            ownerLiens.push({
              sequenceNumber: sr++,
              lienholder: getLienholder(lienItem),
              borrowerDebtor: getBorrowerDebtor(lienItem),
              status: getStatus(lienItem),
              dateRecorded:
                lienItem?.recording_date && lienItem.recording_date !== '0000-00-00'
                  ? dateDash2Slash(lienItem.recording_date)
                  : '',
              dateReleased:
                lienItem?.released?.date && lienItem.released.date !== '0000-00-00'
                  ? dateDash2Slash(lienItem.released.date)
                  : '',
              documentNumber: lienItem?.document_number || '',
              lienAmount: showCurrency(lienItem?.total_lien_judgement_amount),
            });
          }
        });
      }
    });
  }

  countJudgements = 0;
  if (Array.isArray(lienData) && lienData.length > 0) {
    lienData.forEach((owner) => {
      if (owner?.alias_judgements && Array.isArray(owner.alias_judgements)) {
        countJudgements += owner.alias_judgements.length;
      }
    });
  }

  const aliasLiens = [];
  if (Array.isArray(lienData) && lienData.length > 0 && countJudgements > 0) {
    let sr = 1;
    lienData.forEach((owner) => {
      if (
        owner &&
        typeof owner === 'object' &&
        owner?.alias_judgements &&
        Array.isArray(owner.alias_judgements) &&
        owner.alias_judgements.length > 0
      ) {
        owner.alias_judgements.forEach((lienItem) => {
          if (lienItem && typeof lienItem === 'object') {
            aliasLiens.push({
              sequenceNumber: sr++,
              lienholder: getLienholder(lienItem),
              borrowerDebtor: getBorrowerDebtor(lienItem),
              status: getStatus(lienItem),
              dateRecorded:
                lienItem?.recording_date && lienItem.recording_date !== '0000-00-00'
                  ? dateDash2Slash(lienItem.recording_date)
                  : '',
              dateReleased:
                lienItem?.released?.date && lienItem.released.date !== '0000-00-00'
                  ? dateDash2Slash(lienItem.released.date)
                  : '',
              documentNumber: lienItem?.document_number || '',
              lienAmount: showCurrency(lienItem?.total_lien_judgement_amount),
            });
          }
        });
      }
    });
  }

  const currentUnreleasedDeeds = [];
  try {
    const ownerData = sellerData?.OWNER || sellerData?.owner || {};
    const rawMortgageList = Array.isArray(ownerData?.MORTGAGE)
      ? ownerData?.MORTGAGE
      : Array.isArray(ownerData?.mortgage)
        ? ownerData?.mortgage
        : [];

    if (Array.isArray(rawMortgageList) && rawMortgageList?.length > 0) {
      let sr = 1;
      rawMortgageList?.forEach((mortgageItem) => {
        if (!mortgageItem || typeof mortgageItem !== 'object') return;

        const mersMin =
          mortgageItem?.DETECTED_MERS_MIN ||
          mortgageItem?.detectedMersMin ||
          mortgageItem?.detected_mers_min ||
          '';

        const hasMersMin = !!mersMin;

        const bookNumber =
          mortgageItem?.POSITION_MORTGAGE_RECORDED_DOCUMENT_BOOK_NUMBER ||
          mortgageItem?.positionMortgageRecordedDocumentBookNumber ||
          '';
        const pageNumber =
          mortgageItem?.POSITION_MORTGAGE_RECORDED_DOCUMENT_PAGE_NUMBER ||
          mortgageItem?.positionMortgageRecordedDocumentPageNumber ||
          '';

        let bookPage = '';
        if (bookNumber || pageNumber) {
          bookPage = `${bookNumber || ''}${bookNumber && pageNumber ? '/' : ''}${pageNumber || ''}`;
        }

        const rawLoanDate =
          mortgageItem?.POSITION_MORTGAGE_DATE || mortgageItem?.positionMortgageDate || '';
        const loanDate =
          rawLoanDate && rawLoanDate !== '0000-00-00' ? dateDash2Slash(rawLoanDate) : '';

        const rawRecordingDate =
          mortgageItem?.POSITION_MORTGAGE_RECORDING_DATE ||
          mortgageItem?.positionMortgageRecordingDate ||
          '';
        const dateRecorded =
          rawRecordingDate && rawRecordingDate !== '0000-00-00'
            ? dateDash2Slash(rawRecordingDate)
            : '';

        const rawLoanAmount =
          mortgageItem?.POSITION_MORTGAGE_AMOUNT ?? mortgageItem?.positionMortgageAmount ?? null;

        currentUnreleasedDeeds.push({
          sequenceNumber: sr++,
          lender:
            mortgageItem?.POSITION_MORTGAGE_LENDER_NM ||
            mortgageItem?.positionMortgageLenderNm ||
            '',
          loanDate,
          loanType:
            mortgageItem?.POSITION_MORTGAGE_LOAN_TYPE ||
            mortgageItem?.positionMortgageLoanType ||
            '',
          dateRecorded,
          instrumentNumber:
            mortgageItem?.POSITION_MORTGAGE_RECORDED_DOCUMENT_NUMBER ||
            mortgageItem?.positionMortgageRecordedDocumentNumber ||
            '',
          hasDocumentImage:
            mortgageItem?.MORTGAGE_DOCUMENT_IMAGE_ATTACHED === 1 ||
            mortgageItem?.mortgageDocumentImageAttached === 1,
          bookPage,
          loanAmount: showCurrency(rawLoanAmount),
          mersMin,
          hasMersMin,
        });
      });
    }
  } catch (error) {
    // Swallow errors and just fall back to empty deeds data
    console.warn('Error generating current/unreleased deeds data:', error);
  }

  return {
    ownerInvoluntaryLiens: {
      count: ownerLiens.length,
      liens: ownerLiens,
      message:
        ownerLiens.length === 0
          ? 'NONE FOUND. RECOMMEND ADDITIONAL CHECKS INCLUDING BORROWER ALIASES AND/OR RESIDENCE HISTORY'
          : null,
    },
    aliasOtherInvoluntaryLiens: {
      count: aliasLiens.length,
      liens: aliasLiens,
      message:
        aliasLiens.length === 0
          ? 'NONE FOUND. RECOMMEND ADDITIONAL CHECKS INCLUDING BORROWER ALIASES AND/OR RESIDENCE HISTORY'
          : null,
    },
    currentUnreleasedDeeds: {
      count: currentUnreleasedDeeds?.length,
      deeds: currentUnreleasedDeeds,
      message: currentUnreleasedDeeds?.length === 0 ? 'No open mortgages(loans) found.' : null,
    },
  };
};

/**
 * Participant question flows: /questions/{invite|home}/:code, optional /:participantNo,
 * optional /:memberUUID (member segment only when participantNo is present — matches routes).
 */
export function buildQuestionsParticipantBase(base, code, participantNo, memberUUID) {
  if (!code) return base;
  let path = `${base}/${code}`;
  if (participantNo) {
    path += `/${participantNo}`;
    if (memberUUID) {
      path += `/${memberUUID}`;
    }
  }
  return path;
}

export function buildQuestionsInvitePath(code, participantNo, memberUUID, suffix = '') {
  return (
    buildQuestionsParticipantBase('/questions/invite', code, participantNo, memberUUID) +
    (suffix || '')
  );
}

export function buildQuestionsHomePath(code, participantNo, memberUUID, suffix = '') {
  return buildQuestionsParticipantBase('/questions/home', code, participantNo, memberUUID) + suffix;
}

export function buildQuestionsReopenQuerySuffix(reopen_section, element_id) {
  return (
    (reopen_section ? `?reopen_section=${reopen_section}` : '') +
    (element_id ? `&element_id=${element_id}` : '')
  );
}

/**
 * Optional member UUID passed on the DL confirmation return URL (e.g. ?memberUUID=…).
 * Does not change behavior when absent.
 */
export function getQuestionsMemberUUIDFromSearch(search) {
  const params = new URLSearchParams(search || '');
  return params.get('memberUUID') || params.get('member_uuid');
}

//below functions are used to sort summary items in the order of red, yellow/orange, showResolved, green
const getSummaryItemSortRank = (row) =>
  row?.color === 'green'
    ? 3
    : row?.showResolved
      ? 2
      : row?.color === 'red'
        ? 0
        : row?.color === 'yellow' || row?.color === 'orange'
          ? 1
          : 1;

export const sortSummaryItems = (items) =>
  Array.isArray(items)
    ? [...items].sort((a, b) => getSummaryItemSortRank(a) - getSummaryItemSortRank(b))
    : [];
